// src/pages/AIAssistantManagementPage.js
import React, { useState } from 'react';
import { Container, Row, Col, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import { useLanguage } from '../context/LanguageContext.js';
import { useAssistants } from '../hooks/useAssistants.js';
import AssistantCard from '../components/assistant/AssistantCard.js';
import AssistantFormModal from '../components/assistant/AssistantFormModal.js';
import DeleteConfirmationModal from '../components/assistant/DeleteConfirmationModal.js';

const translations = {
  en: {
    title: 'AI Assistants Management',
    createNew: 'Create New Assistant',
    search: 'Search assistants...',
    noAssistants: 'No assistants found',
    createFirst: 'Create your first AI assistant',
    edit: 'Edit',
    test: 'Test',
    assistantCreated: 'Assistant created successfully!',
    assistantUpdated: 'Assistant updated successfully!',
    assistantDeleted: 'Assistant deleted successfully!',
    errorCreating: 'Error creating assistant',
    errorUpdating: 'Error updating assistant',
    errorDeleting: 'Error deleting assistant',
    confirmDelete: 'Confirm Deletion',
    deleteMessage: 'Are you sure you want to delete this assistant?',
    warningMessage: 'This action cannot be undone.',
    yesDelete: 'Yes, Delete',
    cancel: 'Cancel',
    defaultBadge: 'Default',
    activeBadge: 'Active',
    assistantName: 'Assistant Name',
    assistantDescription: 'Description',
    systemPrompt: 'System Prompt',
    saveAssistant: 'Save Assistant',
    saveChanges: 'Save Changes',
    namePlaceholder: 'e.g., Technical Support Agent',
    descriptionPlaceholder: 'Brief description of this assistant\'s purpose (optional)',
    promptPlaceholder: 'Enter the complete system prompt...',
    charactersRemaining: 'characters remaining',
    unsavedChanges: 'You have unsaved changes. Are you sure you want to close?',
    fillRequired: 'Please fill in all required fields',
    tipMessage: 'Tip: Define the assistant\'s role, tone, rules, and response format in the system prompt.',
    createdOn: 'Created',
    lastUpdated: 'Updated',
    editAssistant: 'Edit Assistant',
    toggleActive: 'Active',
    toggleInactive: 'Inactive',
    accessDenied: 'Access Denied - Admin or Manager only'
  },
  pt: {
    title: 'Gerenciamento de Assistentes IA',
    createNew: 'Criar Novo Assistente',
    search: 'Buscar assistentes...',
    noAssistants: 'Nenhum assistente encontrado',
    createFirst: 'Crie seu primeiro assistente IA',
    edit: 'Editar',
    test: 'Testar',
    assistantCreated: 'Assistente criado com sucesso!',
    assistantUpdated: 'Assistente atualizado com sucesso!',
    assistantDeleted: 'Assistente excluído com sucesso!',
    errorCreating: 'Erro ao criar assistente',
    errorUpdating: 'Erro ao atualizar assistente',
    errorDeleting: 'Erro ao excluir assistente',
    confirmDelete: 'Confirmar Exclusão',
    deleteMessage: 'Tem certeza que deseja excluir este assistente?',
    warningMessage: 'Esta ação não pode ser desfeita.',
    yesDelete: 'Sim, Excluir',
    cancel: 'Cancelar',
    defaultBadge: 'Padrão',
    activeBadge: 'Ativo',
    assistantName: 'Nome do Assistente',
    assistantDescription: 'Descrição',
    systemPrompt: 'Prompt do Sistema',
    saveAssistant: 'Salvar Assistente',
    saveChanges: 'Salvar Alterações',
    namePlaceholder: 'ex: Agente de Suporte Técnico',
    descriptionPlaceholder: 'Breve descrição do propósito deste assistente (opcional)',
    promptPlaceholder: 'Digite o prompt completo do sistema...',
    charactersRemaining: 'caracteres restantes',
    unsavedChanges: 'Você tem alterações não salvas. Tem certeza que deseja fechar?',
    fillRequired: 'Por favor, preencha todos os campos obrigatórios',
    tipMessage: 'Dica: Defina o papel, tom, regras e formato de resposta do assistente no prompt do sistema.',
    createdOn: 'Criado',
    lastUpdated: 'Atualizado',
    editAssistant: 'Editar Assistente',
    toggleActive: 'Ativo',
    toggleInactive: 'Inativo',
    accessDenied: 'Acesso Negado - Apenas Admin ou Gerente'
  }
};

export default function AIAssistantManagementPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const {
    assistants,
    loading: assistantsLoading,
    error: assistantsError,
    createAssistant,
    updateAssistant,
    deleteAssistant,
    getAssistantById
  } = useAssistants();

  const [searchQuery, setSearchQuery] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const t = translations[language] || translations.en;

  const isAuthorized = user?.isAdmin || user?.isClientManager;

  if (!isAuthorized) {
    return (
      <Container className="py-4">
        <h5>{t.accessDenied}</h5>
      </Container>
    );
  }

  const filteredAssistants = assistants.filter(assistant =>
    assistant.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreate = () => {
    setSelectedAssistant(null);
    setShowFormModal(true);
  };

  const handleEdit = async (assistant) => {
    try {
      const fullAssistant = await getAssistantById(assistant.id);
      setSelectedAssistant(fullAssistant);
      setShowFormModal(true);
    } catch (error) {
      alert(`${t.errorUpdating}: ${error.message}`);
    }
  };

  const handleDelete = (assistant) => {
    setSelectedAssistant(assistant);
    setShowDeleteModal(true);
  };

  const handleTest = (assistant) => {
    navigate('/admin/ai-assistant', { state: { selectedAssistantId: assistant.id } });
  };

  const handleSaveAssistant = async (formData) => {
    setModalLoading(true);
    try {
      if (selectedAssistant) {
        await updateAssistant(selectedAssistant.id, formData);
        setSuccessMessage(t.assistantUpdated);
      } else {
        await createAssistant(formData);
        setSuccessMessage(t.assistantCreated);
      }
      setShowFormModal(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      alert(`${selectedAssistant ? t.errorUpdating : t.errorCreating}: ${error.message}`);
    } finally {
      setModalLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    setModalLoading(true);
    try {
      await deleteAssistant(selectedAssistant.id);
      setSuccessMessage(t.assistantDeleted);
      setShowDeleteModal(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      alert(`${t.errorDeleting}: ${error.message}`);
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <Container fluid style={{ padding: '20px 40px' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 style={{ fontWeight: 600, color: '#1a1a1a' }}>
            <span style={{ fontSize: '1.5rem', marginRight: '8px' }}>⚙️</span>
            {t.title}
          </h3>
        </div>
        <Button
          variant="primary"
          onClick={handleCreate}
          style={{
            borderRadius: '8px',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(13, 110, 253, 0.3)'
          }}
        >
          ➕ {t.createNew}
        </Button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {assistantsError && (
        <Alert variant="danger">
          Error: {assistantsError}
        </Alert>
      )}

      {/* Search Bar */}
      <Form.Control
        type="text"
        placeholder={t.search}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="mb-4"
        style={{
          maxWidth: '400px',
          borderRadius: '8px',
          border: '2px solid #e0e0e0'
        }}
      />

      {/* Loading */}
      {assistantsLoading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {/* Assistants Grid */}
      {!assistantsLoading && filteredAssistants.length === 0 && (
        <div className="text-center py-5">
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🤖</div>
          <h4 style={{ color: '#666' }}>{t.noAssistants}</h4>
          <p style={{ color: '#999' }}>{t.createFirst}</p>
        </div>
      )}

      {!assistantsLoading && filteredAssistants.length > 0 && (
        <Row className="g-4">
          {filteredAssistants.map(assistant => (
            <Col key={assistant.id} xs={12} md={6} lg={4}>
              <AssistantCard
                assistant={assistant}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onTest={handleTest}
                t={t}
              />
            </Col>
          ))}
        </Row>
      )}

      {/* Form Modal (Create/Edit) */}
      <AssistantFormModal
        show={showFormModal}
        onHide={() => setShowFormModal(false)}
        onSave={handleSaveAssistant}
        assistant={selectedAssistant}
        loading={modalLoading}
        t={t}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        assistant={selectedAssistant}
        loading={modalLoading}
        t={t}
      />
    </Container>
  );
}
